'use client'

import * as Y from 'yjs'
import { atom, createStore, useAtom } from 'jotai'
import { CustomWebsocketProvider } from '~/components/YJSDocument/YJSDocumentProvider/CustomWebsocketProvider'
import { IndexeddbPersistence } from 'y-indexeddb'
import { atomWithStorage } from 'jotai/utils'
import { env } from './env.mjs'
import { CustomDoc } from '~/components/YJSDocument/YJSDocumentProvider/CustomDoc'

export const globalStore = createStore()

export const yjsDocumentsAtom = atom(new Map<string, CustomDoc>())
export const yjsProvidersAtom = atom<Map<string, CustomWebsocketProvider>>(
  new Map()
)

export const yjsDocAtom = (room: string) =>
  atom<CustomDoc>((get) => {
    const yjsDocuments = get(yjsDocumentsAtom)
    let yjsDoc = yjsDocuments.get(room)
    if (!yjsDoc) {
      yjsDoc = new CustomDoc(room, room)
      let initialContent = yjsDoc.get('content', Y.XmlText) as Y.XmlText
      initialContent.insert(0, '')
      yjsDocuments.set(room, yjsDoc)
      new IndexeddbPersistence(room, yjsDoc) // Setup IndexedDB persistence
    }
    return yjsDoc
  })

// Atom to get or create a WebSocket provider for a specific room and Y.Doc
// NOTE: This atom is deprecated - use ProviderManager.getProvider() instead
// Keeping for backwards compatibility but it should not be used
// The memberId must be passed from a React component, not from inside the atom
export const yjsProviderAtom = (room: string, memberId: string = '') =>
  atom<CustomWebsocketProvider>((get) => {
    const providers = get(yjsProvidersAtom)
    const existingProvider = providers.get(room)
    
    // Return existing provider if it exists
    if (existingProvider) {
      return existingProvider
    }
    
    // Create new provider - memberId is required
    if (!memberId) {
      throw new Error(`Cannot create provider for room ${room}: memberId is required`)
    }
    
    const doc = get(yjsDocAtom(room)) // Ensure you have the document instance
    const provider = new CustomWebsocketProvider(
      env.NEXT_PUBLIC_WS_URL,
      room,
      doc,
      memberId,
      {
        connect: false,
      }
    )
    providers.set(room, provider)
    return provider
  })

export type CollapsibleElementVisibility = 'visible' | 'hidden' | 'peek'

export const evaluationSidebarVisibilityAtom =
  atomWithStorage<CollapsibleElementVisibility>(
    'evaluationSidebarVisibility',
    'visible'
  )

export const openAccordions = atom<string[]>([])

export const changelogVisibilityAtom = atom<boolean>(false)

export const currentEvaluationTabAtom = atom<string>('')

export const showPricingModalAtom = atom<boolean>(false)

export const readOnlyAtom = atom<boolean>(false)

export type EvaluationTabConfig = {
  key: string
  label: string
  icon?: string
  type?: string
}

export const evaluationDynamicTabsAtom = atom<EvaluationTabConfig[]>([])
